import {
  useLazyGetCategoriesQuery,
  useLazyGetMakesQuery,
  useLazyGetModelsQuery,
  useLazyIsToolQrCodeAvailableQuery,
  useLazyIsToolSerialNumberAvailableQuery,
} from 'app/services/tools';
import { Tool } from 'app/types/tool';
import { Unit } from 'app/services/units/types';
import { DataForm, FieldProps, FieldType, FileType } from 'components';
import { Drawers } from 'app/types/drawer';
import { formats } from 'utils/datetime';
import moment from 'moment-timezone';
import { useLocalSearch } from 'hooks/useLocalSearch';
import { useTranslation } from 'react-i18next';
import ScannerModal from 'components/DataForm/components/ScannerModal';
import { normalizeFile } from 'utils/file';
import { parseQrValue } from 'utils/qr/parseQrValue';

interface ToolFormProps {
  handleSubmit: any;
  tool?: Tool;
  unit: Unit;
  meta: any;
  redirectTo?: string;
}

const ToolForm = ({
  tool,
  unit,
  handleSubmit,
  meta,
  redirectTo,
}: ToolFormProps) => {
  const unitId = unit.Id;
  const { t } = useTranslation();

  const [ isQrCodeAvailable ] = useLazyIsToolQrCodeAvailableQuery();
  const [ isSerialNumberAvailable ] = useLazyIsToolSerialNumberAvailableQuery();

  const [ searchCatergories ] = useLazyGetCategoriesQuery();
  const { filteredValues: categoryOptions, filter: filterCategories } =
    useLocalSearch({
      initialValuesLoad: () => searchCatergories(unitId),
    });

  const [ searchMakes ] = useLazyGetMakesQuery();
  const { filteredValues: makeOptions, filter: filterMakes } = useLocalSearch({
    initialValuesLoad: () => searchMakes(unitId),
  });

  const [ searchModels ] = useLazyGetModelsQuery();
  const { filteredValues: modelOptions, filter: filterModels } = useLocalSearch(
    {
      initialValuesLoad: () => searchModels(unitId),
    },
  );

  const onSubmit = async (values: any) => {
    await handleSubmit(values);
  };

  const handleQrCodeValidation = async (code: string) =>
    await isQrCodeAvailable({ code, id: tool?.Id }).unwrap();

  const handleSerialNumberValidation = async (serialNumber: string) =>
    await isSerialNumberAvailable({ code: serialNumber, id: tool?.Id }).unwrap();

  const fields: FieldProps[] = [
    {
      name: 'Category',
      label: t('entities.tools.category'),
      type: FieldType.QUICK_SEARCH,
      required: true,
      max: 512,
      searchProps: {
        searchCallback: (value: string) => filterCategories(value),
        options: categoryOptions,
        setDataCallback: undefined,
        localFilter: true,
      },
    },
    {
      name: 'Make',
      label: t('entities.tools.make'),
      type: FieldType.QUICK_SEARCH,
      required: true,
      max: 512,
      searchProps: {
        searchCallback: (value: string) => filterMakes(value),
        options: makeOptions,
        setDataCallback: undefined,
        localFilter: true,
      },
    },
    {
      name: 'Model',
      label: t('entities.tools.model'),
      type: FieldType.QUICK_SEARCH,
      required: true,
      max: 512,
      searchProps: {
        searchCallback: (value: string) => filterModels(value),
        options: modelOptions,
        setDataCallback: undefined,
        localFilter: true,
      },
    },
    {
      name: 'SerialNumber',
      label: t('entities.tools.serialnumber'),
      required: true,
      max: 256,
      validation: {
        validationFunction: handleSerialNumberValidation,
        message: t('forms.tools.serialnumberunavailable'),
      },
    },
    {
      name: 'QrCode',
      label: t('entities.tools.qrcode'),
      placeholder: t('entities.tools.qrcode'),
      max: 512,
      noSpaces: true,
      validation: {
        validationFunction: handleQrCodeValidation,
        message: t('forms.tools.qrcodeunavailable'),
      },
      addOn: <ScannerModal transformValue={(scanned) => parseQrValue(scanned).barcodeValue} />,
    },
    {
      name: 'Power',
      label: t('entities.tools.power'),
      type: FieldType.NUMBER,
      numberProps: { min: 0, max: 99999999.99, controls: false },
      group: 'technical',
      mobileGroup: 'technical',
    },
    {
      name: 'Airflow',
      label: t('entities.tools.airflow'),
      type: FieldType.NUMBER,
      numberProps: { min: 0, max: 99999999.99, controls: false },
      group: 'technical',
      mobileGroup: 'technical',
    },
    {
      name: 'FirstUsageTime',
      label: t('entities.tools.firstusagetime'),
      type: FieldType.DATE,
      datePicker: { dateFormat: formats.DD_MM_YYYY },
    },
    {
      name: 'CertificateFile',
      label: t('entities.tools.certificateurl'),
      type: FieldType.FILE,
      allowedFileType: FileType.PDF,
    },
    {
      name: 'IsActive',
      label: t('entities.tools.active'),
      type: FieldType.BOOLEAN,
      required: true,
    },
  ];

  const initialValues = tool
    ? {
      ...tool,
      FirstUsageTime: tool.FirstUsageTime
        ? moment(tool.FirstUsageTime)
        : undefined,
      CertificateFile: normalizeFile(
        tool?.CertificateFileName ?? 'Certificate',
        tool?.CertificateUrl ?? '',
      ),
    }
    : { IsActive: false };

  return (
    <DataForm
      id={tool ? Drawers.EDIT_TOOL : Drawers.ADD_TOOL}
      entity="tools"
      onSubmit={onSubmit}
      fields={fields}
      meta={meta}
      initialValues={initialValues}
      redirectTo={redirectTo}
    />
  );
};

export default ToolForm;
